import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Tooltip,
  Badge,
  InputAdornment,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as InvoiceIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CurrencyRupee as MoneyIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  AutoAwesome as AutoIcon,
  Close as CloseIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Define invoice structure
interface InvoiceItem {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  patientId: string;
  patientName: string;
  patientToken?: string;
  description?: string;
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  discount: number;
  totalAmount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled';
  serviceType?: 'OP' | 'IP' | 'TEST';
  paymentId?: string;
  issueDate: string;
  dueDate: string;
  isAutoGenerated?: boolean;
  generatedFrom?: 'payment' | 'registration';
  originalId?: string;
  generatedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

const Invoices: React.FC = () => {
  const { state } = useAppContext();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusUpdateInvoice, setStatusUpdateInvoice] = useState<Invoice | null>(null);

  // Create Invoice Form State
  const [createFormData, setCreateFormData] = useState({
    patientType: '', // 'user' or 'ip-op'
    selectedPatient: null as { id: string; name: string; token: string; phone?: string; email?: string } | null,
    description: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [
      {
        id: '1',
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0
      }
    ] as InvoiceItem[],
    notes: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  
  // Real patient data from Firestore
  const [users, setUsers] = useState<any[]>([]);
  const [ipOpPatients, setIpOpPatients] = useState<any[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Fetch real patients from Firestore when modal opens
  useEffect(() => {
    if (showCreateModal) {
      fetchPatients();
    }
  }, [showCreateModal]);

  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      // Fetch registered users from 'users' collection
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.data().firstName || 'Unknown',
        phone: doc.data().phone || doc.data().mobile || 'N/A',
        email: doc.data().email || '',
        token: doc.data().patientId || doc.id
      }));
      setUsers(usersData);
      console.log(`✅ Loaded ${usersData.length} registered users`);

      // Fetch IP/OP patients from 'patients' collection
      const patientsQuery = query(collection(db, 'patients'));
      const patientsSnapshot = await getDocs(patientsQuery);
      const patientsData = patientsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.data().fullName || doc.data().firstName || 'Unknown Patient',
        phone: doc.data().phone || doc.data().phoneNumber || doc.data().mobile || 'N/A',
        email: doc.data().email || '',
        token: doc.data().patientId || doc.id
      }));
      setIpOpPatients(patientsData);
      console.log(`✅ Loaded ${patientsData.length} IP/OP patients`);
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      setSnackbarMessage('Failed to load patients');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Load invoices from Firebase with real-time updates
  useEffect(() => {
    const setupInvoicesData = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Setting up real-time invoices data from Firebase...');
        
        // Set up real-time Firebase listener for invoices
        const q = query(collection(db, 'invoices'));
        const unsubscribeFn = onSnapshot(q, (querySnapshot) => {
          const firebaseInvoices: Invoice[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            firebaseInvoices.push({
              id: doc.id,
              invoiceNumber: data.invoiceNumber || '',
              patientId: data.patientId || '',
              patientName: data.patientName || '',
              patientToken: data.patientToken || '',
              description: data.description || '',
              items: data.items || [],
              subtotal: data.subtotal || 0,
              tax: data.tax || 0,
              discount: data.discount || 0,
              totalAmount: data.totalAmount || 0,
              status: data.status || 'draft',
              serviceType: data.serviceType,
              paymentId: data.paymentId,
              issueDate: data.issueDate || new Date().toISOString().split('T')[0],
              dueDate: data.dueDate || new Date().toISOString().split('T')[0],
              isAutoGenerated: data.isAutoGenerated || false,
              generatedFrom: data.generatedFrom,
              originalId: data.originalId,
              generatedAt: data.generatedAt,
              createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
              updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date()
            });
          });
          console.log('🧾 Received invoices update from Firebase:', firebaseInvoices.length);
          setInvoices(firebaseInvoices);
          setIsLoading(false);
          console.log('✅ Invoices data updated from Firebase');
        });
        
        setUnsubscribe(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up invoices data:', error);
        setIsLoading(false);
        setSnackbarMessage('Failed to load invoices data');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    setupInvoicesData();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('🧹 Cleaning up invoices listener...');
        unsubscribe();
      }
    };
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = invoices;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filterStatus);
    }

    // Search by patient name, invoice number, or description
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.patientName.toLowerCase().includes(query) ||
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        (invoice.description && invoice.description.toLowerCase().includes(query)) ||
        (invoice.patientToken && invoice.patientToken.toLowerCase().includes(query))
      );
    }

    setFilteredInvoices(filtered);
  }, [invoices, filterStatus, searchQuery]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(inv => inv.status === 'paid').length;
    const pendingInvoices = invoices.filter(inv => inv.status === 'pending').length;
    const overdueInvoices = invoices.filter(inv => inv.status === 'overdue').length;
    const draftInvoices = invoices.filter(inv => inv.status === 'draft').length;
    const cancelledInvoices = invoices.filter(inv => inv.status === 'cancelled').length;
    const totalAmount = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    
    return {
      totalInvoices,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      draftInvoices,
      cancelledInvoices,
      totalAmount
    };
  }, [invoices]);

  // Status color helper
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#4CAF50';
      case 'pending': return '#FF9800';
      case 'overdue': return '#F44336';
      case 'cancelled': return '#9E9E9E';
      case 'draft': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  // Service type color helper
  const getServiceTypeColor = (serviceType?: string) => {
    switch (serviceType) {
      case 'OP': return { bg: '#E3F2FD', text: '#1976D2' };
      case 'IP': return { bg: '#FFF3E0', text: '#F57C00' };
      case 'TEST': return { bg: '#F3E5F5', text: '#7B1FA2' };
      default: return { bg: '#F5F5F5', text: '#757575' };
    }
  };

  // Event handlers
  const handleUpdateStatus = (invoice: Invoice) => {
    setStatusUpdateInvoice(invoice);
    setShowStatusDialog(true);
  };

  const handleGeneratePDF = (invoice: Invoice) => {
    // Generate PDF content with invoice details
    const generatePDFContent = () => {
      const pdfContent = `
==============================================
         KBR LIFE CARE HOSPITAL
      INVOICE - ${invoice.invoiceNumber || 'N/A'}
==============================================

Patient Information:
--------------------
Name: ${invoice.patientName || 'N/A'}
${invoice.patientToken ? `Token: ${invoice.patientToken}` : ''}
Patient ID: ${invoice.patientId || 'N/A'}
Service Type: ${invoice.serviceType || 'General'}

Invoice Details:
----------------
Issue Date: ${invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : 'N/A'}
Due Date: ${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
Status: ${invoice.status ? invoice.status.toUpperCase() : 'PENDING'}
${invoice.description ? `Description: ${invoice.description}` : ''}

Items:
------
${invoice.items && invoice.items.length > 0 ? invoice.items.map((item, index) => {
  const qty = Number(item.quantity) || 0;
  const rate = Number(item.rate) || 0;
  const amount = Number(item.amount) || 0;
  return `
${index + 1}. ${item.name || 'Unnamed Item'}
   ${item.description ? `Description: ${item.description}` : ''}
   Quantity: ${qty} x ₹${rate.toFixed(2)} = ₹${amount.toFixed(2)}
`;
}).join('') : 'No items'}

Financial Summary:
------------------
Subtotal:        ₹${(Number(invoice.subtotal) || 0).toFixed(2)}
Tax:             ₹${(Number(invoice.tax) || 0).toFixed(2)}
Discount:       -₹${(Number(invoice.discount) || 0).toFixed(2)}
--------------------------------------------
TOTAL AMOUNT:    ₹${(Number(invoice.totalAmount) || 0).toFixed(2)}

${invoice.isAutoGenerated ? `\nNote: This is an auto-generated invoice from ${invoice.generatedFrom || 'system'}` : ''}

--------------------------------------------
Thank you for choosing KBR Life Care!
For queries, contact: +91-XXXXXXXXXX
Email: info@kbrlifecare.com
==============================================
      `.trim();

      return pdfContent;
    };

    try {
      // Create a downloadable text file as PDF alternative
      const pdfContent = generatePDFContent();
      const blob = new Blob([pdfContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoiceNumber || 'Unknown'}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbarMessage(`PDF generated and downloaded for invoice ${invoice.invoiceNumber}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbarMessage(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    // Create a detailed invoice view in a new window
    const invoiceWindow = window.open('', '_blank', 'width=800,height=600');
    if (invoiceWindow) {
      const invoiceHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice - ${invoice.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .info { margin: 20px 0; }
            .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
            .items { margin: 20px 0; }
            .items table { width: 100%; border-collapse: collapse; }
            .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items th { background-color: #f2f2f2; }
            .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
            .financial { margin: 15px 0; padding: 10px; background: #f9f9f9; border-radius: 5px; }
            .financial-item { display: flex; justify-content: space-between; padding: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>KBR LIFE CARE</h1>
            <h2>Invoice</h2>
          </div>
          <div class="info">
            <div class="info-row"><strong>Invoice Number:</strong> <span>${invoice.invoiceNumber}</span></div>
            <div class="info-row"><strong>Patient Name:</strong> <span>${invoice.patientName}</span></div>
            <div class="info-row"><strong>Patient ID:</strong> <span>${invoice.patientId}</span></div>
            <div class="info-row"><strong>Issue Date:</strong> <span>${new Date(invoice.issueDate).toLocaleDateString()}</span></div>
            <div class="info-row"><strong>Due Date:</strong> <span>${new Date(invoice.dueDate).toLocaleDateString()}</span></div>
            <div class="info-row"><strong>Status:</strong> <span>${invoice.status.toUpperCase()}</span></div>
            ${invoice.serviceType ? `<div class="info-row"><strong>Service Type:</strong> <span>${invoice.serviceType}</span></div>` : ''}
          </div>
          <div class="items">
            <h3>Invoice Items</h3>
            ${invoice.items && invoice.items.length > 0 ? `
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoice.items.map(item => `
                    <tr>
                      <td>${item.name}${item.description ? `<br><small>${item.description}</small>` : ''}</td>
                      <td>${item.quantity}</td>
                      <td>₹${item.rate.toLocaleString()}</td>
                      <td>₹${item.amount.toLocaleString()}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : `<p>No items listed</p>`}
          </div>
          <div class="financial">
            <div class="financial-item"><strong>Subtotal:</strong> <span>₹${invoice.subtotal.toLocaleString()}</span></div>
            <div class="financial-item"><strong>Tax:</strong> <span>₹${invoice.tax.toLocaleString()}</span></div>
            <div class="financial-item"><strong>Discount:</strong> <span>-₹${invoice.discount.toLocaleString()}</span></div>
            <div class="financial-item" style="font-size: 18px; border-top: 2px solid #333; margin-top: 10px; padding-top: 10px;"><strong>Total Amount:</strong> <span>₹${invoice.totalAmount.toLocaleString()}</span></div>
          </div>
          ${invoice.description ? `<div style="margin-top: 20px;"><strong>Note:</strong> ${invoice.description}</div>` : ''}
        </body>
        </html>
      `;
      invoiceWindow.document.write(invoiceHTML);
      invoiceWindow.document.close();
    }
  };

  const handleShareInvoice = async (invoice: Invoice) => {
    const invoiceText = `
KBR LIFE CARE - Invoice
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Invoice Number: ${invoice.invoiceNumber}
Patient: ${invoice.patientName}
Issue Date: ${new Date(invoice.issueDate).toLocaleDateString()}
Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}
Status: ${invoice.status.toUpperCase()}

Items:
${invoice.items && invoice.items.length > 0 ? invoice.items.map((item, index) => 
  `${index + 1}. ${item.name} - Qty: ${item.quantity} x ₹${item.rate} = ₹${item.amount.toLocaleString()}`
).join('\n') : 'No items listed'}

Financial Summary:
Subtotal: ₹${invoice.subtotal.toLocaleString()}
Tax: ₹${invoice.tax.toLocaleString()}
Discount: -₹${invoice.discount.toLocaleString()}
Total Amount: ₹${invoice.totalAmount.toLocaleString()}
${invoice.description ? `\nNote: ${invoice.description}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice - ${invoice.invoiceNumber}`,
          text: invoiceText
        });
        setSnackbarMessage('Invoice shared successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          setSnackbarMessage('Failed to share invoice');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(invoiceText);
        setSnackbarMessage('Invoice copied to clipboard');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        setSnackbarMessage('Failed to copy invoice');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    // Create invoice HTML content
    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${invoice.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
    .info { margin: 20px 0; }
    .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
    .items { margin: 20px 0; }
    .items table { width: 100%; border-collapse: collapse; }
    .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    .items th { background-color: #f2f2f2; }
    .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
    .financial { margin: 15px 0; padding: 10px; background: #f9f9f9; border-radius: 5px; }
    .financial-item { display: flex; justify-content: space-between; padding: 5px 0; }
  </style>
</head>
<body>
  <div class="header">
    <h1>KBR LIFE CARE</h1>
    <h2>Invoice</h2>
  </div>
  <div class="info">
    <div class="info-row"><strong>Invoice Number:</strong> <span>${invoice.invoiceNumber}</span></div>
    <div class="info-row"><strong>Patient Name:</strong> <span>${invoice.patientName}</span></div>
    <div class="info-row"><strong>Patient ID:</strong> <span>${invoice.patientId}</span></div>
    <div class="info-row"><strong>Issue Date:</strong> <span>${new Date(invoice.issueDate).toLocaleDateString()}</span></div>
    <div class="info-row"><strong>Due Date:</strong> <span>${new Date(invoice.dueDate).toLocaleDateString()}</span></div>
    <div class="info-row"><strong>Status:</strong> <span>${invoice.status.toUpperCase()}</span></div>
    ${invoice.serviceType ? `<div class="info-row"><strong>Service Type:</strong> <span>${invoice.serviceType}</span></div>` : ''}
  </div>
  <div class="items">
    <h3>Invoice Items</h3>
    ${invoice.items && invoice.items.length > 0 ? `
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.name}${item.description ? `<br><small>${item.description}</small>` : ''}</td>
              <td>${item.quantity}</td>
              <td>₹${item.rate.toLocaleString()}</td>
              <td>₹${item.amount.toLocaleString()}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    ` : `<p>No items listed</p>`}
  </div>
  <div class="financial">
    <div class="financial-item"><strong>Subtotal:</strong> <span>₹${invoice.subtotal.toLocaleString()}</span></div>
    <div class="financial-item"><strong>Tax:</strong> <span>₹${invoice.tax.toLocaleString()}</span></div>
    <div class="financial-item"><strong>Discount:</strong> <span>-₹${invoice.discount.toLocaleString()}</span></div>
    <div class="financial-item" style="font-size: 18px; border-top: 2px solid #333; margin-top: 10px; padding-top: 10px;"><strong>Total Amount:</strong> <span>₹${invoice.totalAmount.toLocaleString()}</span></div>
  </div>
  ${invoice.description ? `<div style="margin-top: 20px;"><strong>Note:</strong> ${invoice.description}</div>` : ''}
</body>
</html>
    `.trim();

    // Create a Blob from the HTML content
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Invoice_${invoice.patientName.replace(/\s+/g, '_')}_${invoice.invoiceNumber}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSnackbarMessage('Invoice downloaded successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Legacy send invoice for backward compatibility
  const handleSendInvoice = async (invoice: Invoice) => {
    // Use the new share functionality
    await handleShareInvoice(invoice);
  };

  const handleCreateInvoice = () => {
    setShowCreateModal(true);
  };

  // Create Invoice Form Handlers
  const resetCreateForm = () => {
    setCreateFormData({
      patientType: '',
      selectedPatient: null,
      description: '',
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      items: [
        {
          id: '1',
          name: '',
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0
        }
      ],
      notes: ''
    });
  };

  // Get the appropriate patient list based on selected patient type
  const getPatientList = () => {
    if (createFormData.patientType === 'user') {
      return users;
    } else if (createFormData.patientType === 'ip-op') {
      return ipOpPatients;
    }
    return [];
  };

  const calculateItemAmount = (quantity: number, rate: number) => {
    return quantity * rate;
  };

  const updateItem = (index: number, field: string, value: any) => {
    const updatedItems = [...createFormData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Recalculate amount when quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = calculateItemAmount(
        updatedItems[index].quantity,
        updatedItems[index].rate
      );
    }
    
    setCreateFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      name: '',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0
    };
    setCreateFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index: number) => {
    if (createFormData.items.length > 1) {
      const updatedItems = createFormData.items.filter((_, i) => i !== index);
      setCreateFormData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  const calculateTotalAmount = () => {
    return createFormData.items.reduce((sum, item) => sum + item.amount, 0);
  };

  const handleSubmitCreateInvoice = async () => {
    // Validate patient type selection
    if (!createFormData.patientType) {
      setSnackbarMessage('Please select patient type');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validate form
    if (!createFormData.selectedPatient) {
      setSnackbarMessage('Please select a patient');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!createFormData.description.trim()) {
      setSnackbarMessage('Please enter invoice description');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!createFormData.dueDate) {
      setSnackbarMessage('Please select due date');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Check if at least one item has valid data
    const validItems = createFormData.items.filter(item => item.name.trim() && item.rate > 0);
    if (validItems.length === 0) {
      setSnackbarMessage('Please add at least one valid item');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsCreating(true);

    try {
      const totalAmount = calculateTotalAmount();
      
      // Generate unique invoice number
      const timestamp = Date.now();
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(timestamp).slice(-6)}`;
      
      // Prepare invoice data for Firebase
      const invoiceData = {
        invoiceNumber,
        patientId: createFormData.selectedPatient.id,
        patientName: createFormData.selectedPatient.name,
        patientPhone: createFormData.selectedPatient.phone,
        patientEmail: createFormData.selectedPatient.email || '',
        patientToken: createFormData.selectedPatient.token || '',
        patientType: createFormData.patientType, // 'user' or 'ip-op'
        description: createFormData.description.trim(),
        items: validItems,
        subtotal: totalAmount,
        tax: 0,
        discount: 0,
        totalAmount,
        status: 'draft',
        issueDate: createFormData.issueDate,
        dueDate: createFormData.dueDate,
        notes: createFormData.notes || '',
        isAutoGenerated: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('📄 Creating invoice in Firebase:', invoiceData);

      // Save to Firebase
      const docRef = await addDoc(collection(db, 'invoices'), invoiceData);
      
      console.log('✅ Invoice created successfully with ID:', docRef.id);

      // Close modal and reset form
      setShowCreateModal(false);
      resetCreateForm();
      
      setSnackbarMessage(`Invoice ${invoiceNumber} created successfully!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // The real-time listener will automatically update the UI
    } catch (error: any) {
      console.error('❌ Error creating invoice:', error);
      setSnackbarMessage(`Failed to create invoice: ${error?.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsCreating(false);
    }
  };

  // Set default due date to +30 days when issue date changes
  useEffect(() => {
    if (createFormData.issueDate && !createFormData.dueDate) {
      const issueDate = new Date(createFormData.issueDate);
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + 30);
      setCreateFormData(prev => ({
        ...prev,
        dueDate: dueDate.toISOString().split('T')[0]
      }));
    }
  }, [createFormData.issueDate, createFormData.dueDate]);

  const handleUpdateStatusAction = async (newStatus: Invoice['status']) => {
    if (!statusUpdateInvoice) return;

    try {
      // Update in Firebase
      const invoiceRef = doc(db, 'invoices', statusUpdateInvoice.id);
      await updateDoc(invoiceRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      setShowStatusDialog(false);
      setStatusUpdateInvoice(null);
      
      setSnackbarMessage(`Invoice ${statusUpdateInvoice.invoiceNumber} status changed to ${newStatus.toUpperCase()}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Error updating invoice status:', error);
      setSnackbarMessage(`Failed to update status: ${error?.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteInvoiceAction = async (invoice: Invoice) => {
    if (window.confirm(`Are you sure you want to permanently delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`)) {
      try {
        console.log('🗑️ Deleting invoice from Firebase:', invoice.id);
        
        // Delete from Firebase
        const invoiceRef = doc(db, 'invoices', invoice.id);
        await deleteDoc(invoiceRef);
        
        // Update local state immediately for better UX
        setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
        
        setSnackbarMessage(`Invoice ${invoice.invoiceNumber} deleted successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        console.log('✅ Invoice deleted successfully from web dashboard');
      } catch (error: any) {
        console.error('❌ Error deleting invoice:', error);
        setSnackbarMessage(`Failed to delete invoice: ${error?.message || 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
            Invoice Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create & manage billing invoices
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateInvoice}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 2, 
        mb: 3 
      }}>
        <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Avatar sx={{ bgcolor: '#dc3545', mx: 'auto', mb: 1, width: 48, height: 48 }}>
            <InvoiceIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {stats.totalInvoices}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
        </Card>
        <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Avatar sx={{ bgcolor: '#28a745', mx: 'auto', mb: 1, width: 48, height: 48 }}>
            <CheckCircleIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {stats.paidInvoices}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Paid
          </Typography>
        </Card>
        <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Avatar sx={{ bgcolor: '#ffc107', mx: 'auto', mb: 1, width: 48, height: 48 }}>
            <ScheduleIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {stats.pendingInvoices}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pending
          </Typography>
        </Card>
        <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Avatar sx={{ bgcolor: '#dc3545', mx: 'auto', mb: 1, width: 48, height: 48 }}>
            <WarningIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {stats.overdueInvoices}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Overdue
          </Typography>
        </Card>
        <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
          <Avatar sx={{ bgcolor: '#007bff', mx: 'auto', mb: 1, width: 48, height: 48 }}>
            <MoneyIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            ₹{stats.totalAmount.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Value
          </Typography>
        </Card>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search invoices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>
        <Tabs
          value={filterStatus}
          onChange={(_, value) => setFilterStatus(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          <Tab label="Draft" value="draft" />
          <Tab label="Pending" value="pending" />
          <Tab label="Paid" value="paid" />
          <Tab label="Overdue" value="overdue" />
          <Tab label="Cancelled" value="cancelled" />
        </Tabs>
      </Paper>

      {/* Invoices List */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        {filteredInvoices.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Invoices Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first invoice to get started'
              }
            </Typography>
          </Paper>
        ) : (
          filteredInvoices.map((invoice) => (
            <Card key={invoice.id} sx={{ p: 2 }}>
              <CardContent sx={{ pb: '16px !important' }}>
                {/* Invoice Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, position: 'relative' }}>
                  {/* Auto-generated badge */}
                  {invoice.isAutoGenerated && (
                    <Chip
                      icon={<AutoIcon />}
                      label="Auto-Generated"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  
                  <Avatar sx={{ bgcolor: '#f8f9fa', mr: 2, width: 40, height: 40 }}>
                    <InvoiceIcon color="action" />
                  </Avatar>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {invoice.invoiceNumber}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {invoice.patientName}
                    </Typography>
                    {invoice.patientToken && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Token: {invoice.patientToken}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {invoice.description || 'No description'}
                    </Typography>
                    
                    {/* Service Type Indicator */}
                    {invoice.serviceType && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                        <Chip
                          label={invoice.serviceType}
                          size="small"
                          sx={{
                            backgroundColor: getServiceTypeColor(invoice.serviceType).bg,
                            color: getServiceTypeColor(invoice.serviceType).text,
                            fontSize: '0.75rem',
                            height: 20
                          }}
                        />
                        {invoice.paymentId && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Linked to Payment
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ₹{invoice.totalAmount.toLocaleString()}
                    </Typography>
                    <Chip
                      label={invoice.status.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(invoice.status),
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  </Box>
                </Box>

                {/* Invoice Metadata */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Issue: {invoice.issueDate}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Due: {invoice.dueDate}
                    </Typography>
                  </Box>
                </Box>

                {/* Invoice Items Preview */}
                {invoice.items && invoice.items.length > 0 && (
                  <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
                      Items ({invoice.items.length}):
                    </Typography>
                    {invoice.items.slice(0, 2).map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          • {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          ₹{item.amount.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                    {invoice.items.length > 2 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        +{invoice.items.length - 2} more items
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Auto-Generated Details */}
                {invoice.isAutoGenerated && (
                  <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f8f4ff', borderLeft: '3px solid #7c3aed' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#7c3aed', mb: 1, display: 'block' }}>
                      Auto-Generated Invoice
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Generated From:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {invoice.generatedFrom === 'payment' ? 'Payment Addition' : 'Patient Registration'}
                      </Typography>
                    </Box>
                    {invoice.originalId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Source ID:
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#9ca3af' }}>
                          {invoice.originalId}
                        </Typography>
                      </Box>
                    )}
                    {invoice.generatedAt && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Generated At:
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {invoice.generatedAt.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Action Buttons */}
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Tooltip title="Update Status">
                    <IconButton
                      size="small"
                      onClick={() => handleUpdateStatus(invoice)}
                      sx={{ color: '#ed6c02', backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#fff3e0' } }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Invoice">
                    <IconButton
                      size="small"
                      onClick={() => handleViewInvoice(invoice)}
                      sx={{ color: '#1976d2', backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#e3f2fd' } }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Invoice">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadInvoice(invoice)}
                      sx={{ color: '#2e7d32', backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#e8f5e8' } }}
                    >
                      <PdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Invoice">
                    <IconButton
                      size="small"
                      onClick={() => handleShareInvoice(invoice)}
                      sx={{ color: '#8B5CF6', backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#F5F3FF' } }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteInvoiceAction(invoice)}
                      sx={{ color: '#d32f2f', backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#ffebee' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Invoice Details Modal */}
      <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Invoice Details - {selectedInvoice?.invoiceNumber}
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                gap: 2, 
                mb: 2 
              }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Patient Information</Typography>
                  <Typography variant="body2"><strong>Name:</strong> {selectedInvoice.patientName}</Typography>
                  {selectedInvoice.patientToken && (
                    <Typography variant="body2"><strong>Token:</strong> {selectedInvoice.patientToken}</Typography>
                  )}
                  <Typography variant="body2"><strong>Service Type:</strong> {selectedInvoice.serviceType || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Invoice Information</Typography>
                  <Typography variant="body2"><strong>Issue Date:</strong> {selectedInvoice.issueDate}</Typography>
                  <Typography variant="body2"><strong>Due Date:</strong> {selectedInvoice.dueDate}</Typography>
                  <Typography variant="body2"><strong>Status:</strong> {selectedInvoice.status.toUpperCase()}</Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Items</Typography>
              <Paper sx={{ p: 1 }}>
                {selectedInvoice.items.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderBottom: index < selectedInvoice.items.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    <Box>
                      <Typography variant="body2"><strong>{item.name}</strong></Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2">{item.quantity} × ₹{item.rate}</Typography>
                      <Typography variant="body2"><strong>₹{item.amount}</strong></Typography>
                    </Box>
                  </Box>
                ))}
                
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">₹{selectedInvoice.subtotal}</Typography>
                  </Box>
                  {selectedInvoice.tax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Tax:</Typography>
                      <Typography variant="body2">₹{selectedInvoice.tax}</Typography>
                    </Box>
                  )}
                  {selectedInvoice.discount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Discount:</Typography>
                      <Typography variant="body2">-₹{selectedInvoice.discount}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', pt: 1 }}>
                    <Typography variant="h6"><strong>Total:</strong></Typography>
                    <Typography variant="h6"><strong>₹{selectedInvoice.totalAmount}</strong></Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInvoice && (
            <>
              <Button onClick={() => handleViewInvoice(selectedInvoice)} startIcon={<VisibilityIcon />} variant="contained" color="primary">
                View Invoice
              </Button>
              <Button onClick={() => handleDownloadInvoice(selectedInvoice)} startIcon={<PdfIcon />} variant="contained" color="success">
                Download
              </Button>
              <Button onClick={() => handleShareInvoice(selectedInvoice)} startIcon={<SendIcon />} variant="contained" sx={{ backgroundColor: '#8B5CF6', '&:hover': { backgroundColor: '#7C3AED' } }}>
                Share
              </Button>
              <Button onClick={() => setShowDetailsModal(false)} variant="outlined">Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetCreateForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Create Invoice
          </Typography>
          <IconButton 
            onClick={() => {
              setShowCreateModal(false);
              resetCreateForm();
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Patient Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Patient Type *
              </Typography>
              <TextField
                select
                fullWidth
                value={createFormData.patientType}
                onChange={(e) => {
                  setCreateFormData(prev => ({ 
                    ...prev, 
                    patientType: e.target.value,
                    selectedPatient: null // Reset patient when type changes
                  }));
                }}
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
              >
                <option value="">Select Patient Type</option>
                <option value="user">Registered User</option>
                <option value="ip-op">IP/OP Patient</option>
              </TextField>
            </Box>

            {/* Select Patient - Show only if patient type is selected */}
            {createFormData.patientType && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {createFormData.patientType === 'user' ? 'Select Registered User' : 'Select IP/OP Patient'} * 
                  {loadingPatients ? ' (Loading...)' : ` (${getPatientList().length} available)`}
                </Typography>
                <Autocomplete
                  value={createFormData.selectedPatient}
                  onChange={(_, newValue) => {
                    setCreateFormData(prev => ({ ...prev, selectedPatient: newValue }));
                  }}
                  options={getPatientList()}
                  getOptionLabel={(option) => option.name}
                  disabled={loadingPatients || getPatientList().length === 0}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40 }}>
                        {option.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.phone} {option.token && `• ${option.token}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={loadingPatients ? "Loading patients..." : "Search and select patient"}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  noOptionsText={loadingPatients ? "Loading..." : "No patients found"}
                />
                {createFormData.selectedPatient && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="primary">
                      <strong>Selected:</strong> {createFormData.selectedPatient.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Phone: {createFormData.selectedPatient.phone}
                      {createFormData.selectedPatient.email && ` • Email: ${createFormData.selectedPatient.email}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Invoice Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Invoice Description *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., Medical Consultation & Treatment"
                value={createFormData.description}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                variant="outlined"
              />
            </Box>

            {/* Issue Date and Due Date */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Issue Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={createFormData.issueDate}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Due Date *
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={createFormData.dueDate}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    variant="outlined"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const issueDate = new Date(createFormData.issueDate);
                      const dueDate = new Date(issueDate);
                      dueDate.setDate(dueDate.getDate() + 30);
                      setCreateFormData(prev => ({
                        ...prev,
                        dueDate: dueDate.toISOString().split('T')[0]
                      }));
                    }}
                    sx={{ minWidth: '60px' }}
                  >
                    +30d
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Invoice Items */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Invoice Items *
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  size="small"
                  sx={{ 
                    bgcolor: '#9c27b0', 
                    '&:hover': { bgcolor: '#7b1fa2' },
                    borderRadius: 2
                  }}
                >
                  Add Item
                </Button>
              </Box>

              {createFormData.items.map((item, index) => (
                <Card key={item.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Item {index + 1}
                      </Typography>
                      {createFormData.items.length > 1 && (
                        <IconButton
                          onClick={() => removeItem(index)}
                          color="error"
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Item name *"
                        placeholder="Item name *"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Item description (optional)"
                        placeholder="Item description (optional)"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          label="Qty"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 1 }}
                          sx={{ width: '80px' }}
                        />
                        <Typography variant="h6">×</Typography>
                        <TextField
                          label="Rate"
                          type="number"
                          value={item.rate}
                          onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: '100px' }}
                        />
                        <Typography variant="h6">=</Typography>
                        <TextField
                          label="Amount"
                          value={item.amount}
                          variant="outlined"
                          size="small"
                          disabled
                          InputProps={{
                            startAdornment: <Typography variant="body2">₹</Typography>
                          }}
                          sx={{ width: '100px' }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {/* Total Amount */}
              <Card sx={{ bgcolor: '#f5f5f5', border: '2px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Total Amount:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                      ₹{calculateTotalAmount()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Notes */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Notes (Optional)
              </Typography>
              <TextField
                fullWidth
                placeholder="Additional notes for the patient"
                value={createFormData.notes}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, notes: e.target.value }))}
                variant="outlined"
                multiline
                rows={3}
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => {
              setShowCreateModal(false);
              resetCreateForm();
            }}
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitCreateInvoice}
            variant="contained"
            disabled={isCreating}
            startIcon={isCreating ? <CircularProgress size={16} /> : <InvoiceIcon />}
            sx={{ 
              minWidth: 160,
              bgcolor: '#9c27b0', 
              '&:hover': { bgcolor: '#7b1fa2' },
              '&:disabled': { bgcolor: '#e0e0e0' }
            }}
          >
            {isCreating ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog 
        open={showStatusDialog} 
        onClose={() => {
          setShowStatusDialog(false);
          setStatusUpdateInvoice(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Update Invoice Status
        </DialogTitle>
        <DialogContent>
          {statusUpdateInvoice && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current status: <strong>{statusUpdateInvoice.status.toUpperCase()}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Select new status for invoice <strong>{statusUpdateInvoice.invoiceNumber}</strong>:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { label: 'Draft', value: 'draft', color: '#9e9e9e' },
                  { label: 'Pending', value: 'pending', color: '#ff9800' },
                  { label: 'Paid', value: 'paid', color: '#4caf50' },
                  { label: 'Overdue', value: 'overdue', color: '#f44336' },
                  { label: 'Cancelled', value: 'cancelled', color: '#757575' }
                ]
                  .filter(status => status.value !== statusUpdateInvoice.status)
                  .map(status => (
                    <Button
                      key={status.value}
                      variant="outlined"
                      fullWidth
                      onClick={() => handleUpdateStatusAction(status.value as Invoice['status'])}
                      sx={{
                        borderColor: status.color,
                        color: status.color,
                        justifyContent: 'flex-start',
                        px: 2,
                        py: 1.5,
                        '&:hover': {
                          borderColor: status.color,
                          bgcolor: `${status.color}15`,
                        }
                      }}
                    >
                      {status.label}
                    </Button>
                  ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowStatusDialog(false);
              setStatusUpdateInvoice(null);
            }}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoices;